/**
 * ProjectsBoard — the issue-centric Work ▸ Projects surface (Matt, 2026-07-16:
 * "roll issues into projects … create new projects and have tasks automatically
 * roll into them"). A VERTICAL stack of collapsible project groups, each showing
 * its open issues.
 *
 * ── One convention, no bespoke drag (2026-07-23) ─────────────────────────────
 * This used to be its own thing: a `@dnd-kit` drag surface with a grip handle and
 * a "Move" overlay menu on every row, so /work/projects looked and behaved unlike
 * every other work list in the app (Matt, 2026-07-23: "seems to use a completely
 * different convention here, with draggable items and weird things — make them
 * all consistent"). It is now exactly what the Issues queue is: a stack of
 * `WorkGroup` sections, each rendering the SHARED `ScopedTaskLifecycleList` — the
 * one `ScopedTask` → `WorkRow` renderer the queue and every mode dashboard use.
 * So a task is byte-identical here and on the queue: the priority signifier, the
 * status ring, the section pill, the owner avatar, the review→"Needs your verify"
 * blocked mark, the date column — the full row, not the thinner {title,status}
 * one this surface used to hand-roll (Matt, 2026-07-23: "those are issues right?
 * why aren't we seeing the full issues card there?"). A row opens the task peek in
 * place (`?task=<id>`), the same doorway; re-home an issue from that peek.
 *
 * Pure presentation (display-consistency rail): receives already-shaped groups +
 * the create callback, never fetches or formats. The mount (`WorkProjectsView`)
 * supplies the `ScopedTask` rows + the server action.
 */

import * as React from "react";
import { useNavigate as useRouter } from "../../router";
import { Button } from "../../button";
import { Input } from "../../input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../dialog";
import { EmptyState } from "../empty-state";
import { Plus, FolderPlus } from "../../icons";
import { ShellActionsPortal } from "../../shell-title-context";
import { WorkGroup } from "../work-group";
import { ScopedTaskLifecycleList } from "./scoped-task-lifecycle-list";
import type { ScopedTask } from "../../types/projects";

export interface ProjectsBoardGroup {
  slug: string;
  name: string;
  color: string | null;
  openCount: number;
  reviewCount: number;
  /** FULL scoped rows — the SAME shape the Issues queue renders, so the row is
   *  identical on both surfaces (loaded by `loadProjectsBoard`). */
  issues: ScopedTask[];
}

export interface ProjectsBoardProps {
  groups: ProjectsBoardGroup[];
  /** Create a new project by name; returns its slug on success. */
  onCreateProject: (name: string) => Promise<{ ok: boolean; slug?: string }>;
}

export function ProjectsBoard({ groups, onCreateProject }: ProjectsBoardProps) {
  return (
    <div className="flex flex-col gap-3 px-6 pb-8">
      {/* The create action rides the masthead title row (inline with "Projects"),
          hoisted through the shell title portal — no in-body header, no restated
          "issues live in a project" lede (that's the masthead description's job). */}
      <ShellActionsPortal>
        <NewProjectButton onCreateProject={onCreateProject} />
      </ShellActionsPortal>

      <div className="flex flex-col gap-3">
        {groups.map((g) => {
          const overflow = g.openCount > g.issues.length ? g.openCount - g.issues.length : 0;
          return (
            <WorkGroup
              key={g.slug}
              title={
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: g.color ?? "var(--color-muted-foreground)" }}
                  />
                  <span className="truncate">{g.name}</span>
                </span>
              }
              count={g.openCount}
              aside={g.reviewCount > 0 ? `${g.reviewCount} in review` : undefined}
            >
              {/* The SHARED task renderer — project scope, so the one surviving
                  context pill is the SECTION (the project is already the group). */}
              <ScopedTaskLifecycleList
                tasks={g.issues}
                scope={{ kind: "project", projectSlug: g.slug }}
                gutter
                empty="No open issues"
              />
              {overflow > 0 ? (
                <div className="border-t border-border px-4 py-2 type-subtext text-muted-foreground">
                  and {overflow} more…
                </div>
              ) : null}
            </WorkGroup>
          );
        })}
        {groups.length === 0 ? (
          <EmptyState
            variant="inline"
            icon={<FolderPlus className="size-5" />}
            title="No projects yet"
            description="Create a project to start organizing your issues."
          />
        ) : null}
      </div>
    </div>
  );
}

function NewProjectButton({ onCreateProject }: { onCreateProject: ProjectsBoardProps["onCreateProject"] }) {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    const res = await onCreateProject(trimmed);
    setBusy(false);
    if (res.ok) {
      setOpen(false);
      setName("");
      router.refresh();
    }
  }

  return (
    <>
      {/* Create is a bare "+" (Matt, 2026-07-23: "a + new button should always
          just be a +, not 'New project'/'New goal'") — one create grammar app-wide.
          The label lives on the tooltip/aria, not in the button. */}
      <Button
        variant="default"
        size="icon-tb"
        aria-label="New project"
        title="New project"
        onClick={() => setOpen(true)}
      >
        <Plus className="size-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
            <DialogDescription>
              Create a project. New issues you add under it — and any you move in — roll up here.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Project name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="default" disabled={!name.trim() || busy} onClick={() => void submit()}>
              {busy ? "Creating…" : "Create project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
